import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t, type IntlService } from 'ember-intl';
import Select from 'tio-ui/components/select';
import {
  FORMAT_OPTIONS,
  PROGRAM_OPTIONS,
  SAVINGS_OPTIONS,
  STUDY_AREA_OPTIONS,
} from 'tio-common/utils/lpn-options';
import type { LpnPageFilters } from 'tio-data/requests/lpn-page';

/** The multi-select LPN filters; `search` is handled separately by each surface. */
export type LpnFilterValues = Required<Omit<LpnPageFilters, 'search'>>;
/** One of the multi-select filters. */
export type LpnFilterKey = keyof LpnFilterValues;

/** No filter selected. Treat as read-only: replace it, never mutate its arrays. */
export const EMPTY_LPN_FILTERS: LpnFilterValues = {
  savings: [],
  format: [],
  programs: [],
  areasOfStudy: [],
};

interface LpnFilterFieldsSignature {
  Args: {
    /** The current selection for every filter. */
    values: LpnFilterValues;
    /** Called with the full new selection for one filter. */
    onChange: (key: LpnFilterKey, keys: string[]) => void;
  };
}

/** The savings, format, programs and areas-of-study selects shared by /lpn and the TA dashboard drawer. */
export default class LpnFilterFields extends Component<LpnFilterFieldsSignature> {
  @service declare intl: IntlService;

  #translate(options: { key: string; labelKey: string }[]) {
    return options.map((o) => ({ key: o.key, label: this.intl.t(o.labelKey) }));
  }

  get savingsOptions() {
    return this.#translate(SAVINGS_OPTIONS);
  }

  get formatOptions() {
    return this.#translate(FORMAT_OPTIONS);
  }

  get programsOptions() {
    return this.#translate(PROGRAM_OPTIONS);
  }

  get areasOfStudyOptions() {
    return this.#translate(STUDY_AREA_OPTIONS);
  }

  setSavings = (keys: string[]) => this.args.onChange('savings', keys);
  setFormat = (keys: string[]) => this.args.onChange('format', keys);
  setPrograms = (keys: string[]) => this.args.onChange('programs', keys);
  setAreasOfStudy = (keys: string[]) => this.args.onChange('areasOfStudy', keys);

  <template>
    <div class="space-y-6">
      <Select
        @label={{t "lpn.filters.savings"}}
        @items={{this.savingsOptions}}
        @multiple={{true}}
        @selectedKeys={{@values.savings}}
        @onChangeKeys={{this.setSavings}}
        @placeholder={{t "lpn.filters.savings_placeholder"}}
      />
      <Select
        @label={{t "lpn.filters.format"}}
        @items={{this.formatOptions}}
        @multiple={{true}}
        @selectedKeys={{@values.format}}
        @onChangeKeys={{this.setFormat}}
        @placeholder={{t "lpn.filters.format_placeholder"}}
      />
      <Select
        @label={{t "lpn.filters.programs"}}
        @items={{this.programsOptions}}
        @multiple={{true}}
        @selectedKeys={{@values.programs}}
        @onChangeKeys={{this.setPrograms}}
        @placeholder={{t "lpn.filters.programs_placeholder"}}
      />
      <Select
        @label={{t "lpn.filters.areas_of_study"}}
        @items={{this.areasOfStudyOptions}}
        @multiple={{true}}
        @selectedKeys={{@values.areasOfStudy}}
        @onChangeKeys={{this.setAreasOfStudy}}
        @placeholder={{t "lpn.filters.areas_of_study_placeholder"}}
      />
    </div>
  </template>
}
